import React from 'react';

interface CurriculumMapPreviewProps {
  onClick?: () => void;
}

const CurriculumMapPreview: React.FC<CurriculumMapPreviewProps> = ({ onClick }) => {
  // Node colors matching site theme by pillar
  const colors = {
    numbers_algebra: '#10b981', // emerald
    measurement_space: '#0ea5e9', // blue
    statistics: '#f43f5e', // rose
    calculus: '#8b5cf6', // violet
    financial: '#f59e0b', // amber
    fractions: '#06b6d4', // cyan
  };

  // Preview data showing K-12 progression (simplified)
  const yearData = [
    { year: 'K', nodes: [colors.numbers_algebra, colors.numbers_algebra, colors.measurement_space, colors.measurement_space, colors.statistics, colors.fractions] },
    { year: 'Y2', nodes: [colors.numbers_algebra, colors.numbers_algebra, colors.fractions, colors.measurement_space, colors.measurement_space, colors.statistics] },
    { year: 'Y4', nodes: [colors.numbers_algebra, colors.numbers_algebra, colors.fractions, colors.numbers_algebra, colors.measurement_space, colors.statistics] },
    { year: 'Y6', nodes: [colors.numbers_algebra, colors.fractions, colors.numbers_algebra, colors.measurement_space, colors.measurement_space, colors.statistics] },
    { year: 'Y8', nodes: [colors.numbers_algebra, colors.numbers_algebra, colors.numbers_algebra, colors.measurement_space, colors.measurement_space, colors.statistics] },
    { year: 'Y10', nodes: [colors.numbers_algebra, colors.numbers_algebra, colors.measurement_space, colors.statistics, colors.financial] },
    { year: 'Y12', nodes: [colors.numbers_algebra, colors.calculus, colors.calculus, colors.calculus, colors.statistics, colors.financial] },
  ];

  const width = 320;
  const height = 280;
  const padding = 12;
  const yearLabelWidth = 28;
  const rowHeight = 36;
  const nodeRadius = 4;
  const nodeGap = 40;
  const startY = 24;

  return (
    <div
      className="relative cursor-pointer transition-all duration-300 hover:scale-[1.02] group"
      onClick={onClick}
      style={{
        background: 'linear-gradient(180deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        className="px-4 py-2 border-b"
        style={{
          borderColor: 'rgba(255, 255, 255, 0.1)',
          background: 'rgba(14, 165, 233, 0.05)',
        }}
      >
        <span className="text-xs font-semibold" style={{ color: '#94a3b8' }}>
          K-12 Curriculum Knowledge Tree
        </span>
      </div>

      {/* SVG Content */}
      <svg width={width} height={height} className="block">
        <defs>
          <filter id="preview-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <linearGradient id="fade-gradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="transparent" />
            <stop offset="100%" stopColor="rgba(15, 23, 42, 0.98)" />
          </linearGradient>
          <marker
            id="preview-arrow"
            markerWidth="4"
            markerHeight="4"
            refX="3"
            refY="2"
            orient="auto"
          >
            <polygon points="0 0, 4 2, 0 4" fill="#475569" />
          </marker>
        </defs>

        {/* Vertical progression lines (showing prerequisite flow) */}
        {yearData.slice(0, -1).map((row, rowIndex) => {
          const nextRow = yearData[rowIndex + 1];
          const minNodes = Math.min(row.nodes.length, nextRow.nodes.length, 4);

          return Array.from({ length: minNodes }).map((_, col) => {
            const x = yearLabelWidth + padding + col * nodeGap;
            const y1 = startY + rowIndex * rowHeight + nodeRadius + 2;
            const y2 = startY + (rowIndex + 1) * rowHeight - nodeRadius - 2;

            return (
              <line
                key={`conn-${rowIndex}-${col}`}
                x1={x}
                y1={y1}
                x2={x}
                y2={y2}
                stroke="#475569"
                strokeWidth="1"
                markerEnd="url(#preview-arrow)"
                opacity="0.5"
              />
            );
          });
        })}

        {/* Cross-topic link example (showing algebra -> calculus progression) */}
        <path
          d="M 92 168 Q 92 186 132 186 Q 172 186 172 204"
          fill="none"
          stroke="#8b5cf6"
          strokeWidth="1.5"
          strokeDasharray="3 2"
          opacity="0.6"
        />

        {/* Stage separator lines */}
        <line
          x1={padding}
          y1={startY + 4 * rowHeight - rowHeight / 2}
          x2={width - padding}
          y2={startY + 4 * rowHeight - rowHeight / 2}
          stroke="#334155"
          strokeWidth="1"
          strokeDasharray="3 3"
          opacity="0.4"
        />
        <text
          x={width - padding - 4}
          y={startY + 4 * rowHeight - rowHeight / 2 - 4}
          fill="#64748b"
          fontSize="7"
          fontFamily="system-ui"
          textAnchor="end"
        >
          High School →
        </text>

        {/* Year labels and nodes */}
        {yearData.map((row, rowIndex) => (
          <g key={row.year}>
            {/* Year label */}
            <text
              x={padding}
              y={startY + rowIndex * rowHeight + 4}
              fill="#94a3b8"
              fontSize="9"
              fontWeight="700"
              fontFamily="system-ui, -apple-system, sans-serif"
            >
              {row.year}
            </text>

            {/* Nodes */}
            {row.nodes.map((color, nodeIndex) => {
              const cx = yearLabelWidth + padding + nodeIndex * nodeGap;
              const cy = startY + rowIndex * rowHeight;
              return (
                <g key={`${row.year}-${nodeIndex}`}>
                  {/* Node dot with glow */}
                  <circle
                    cx={cx}
                    cy={cy}
                    r={nodeRadius}
                    fill={color}
                    filter="url(#preview-glow)"
                  />
                  {/* Short label placeholder line */}
                  <line
                    x1={cx + nodeRadius + 3}
                    y1={cy}
                    x2={cx + nodeRadius + 20}
                    y2={cy}
                    stroke="#334155"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                </g>
              );
            })}
          </g>
        ))}

        {/* Fade overlay at bottom */}
        <rect
          x="0"
          y={height - 50}
          width={width}
          height="50"
          fill="url(#fade-gradient)"
        />

        {/* Stats overlay */}
        <g transform={`translate(${width - 90}, ${height - 45})`}>
          <text fill="#64748b" fontSize="8" fontFamily="system-ui">
            13 Year Levels
          </text>
          <text y="12" fill="#64748b" fontSize="8" fontFamily="system-ui">
            150+ Modules
          </text>
          <text y="24" fill="#64748b" fontSize="8" fontFamily="system-ui">
            Linked Prerequisites
          </text>
        </g>
      </svg>

      {/* Play/expand overlay */}
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/20">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center"
          style={{
            background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.9) 0%, rgba(6, 182, 212, 0.9) 100%)',
            boxShadow: '0 8px 32px rgba(14, 165, 233, 0.4)',
          }}
        >
          <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default CurriculumMapPreview;
